/**
 * App Component - Root component con providers e routing principale
 */

import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import DocumentiSezioneBox from './components/common/DocumentiSezioneBox';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { UIProvider, useUI } from './contexts/UIContext';
import { ModalProvider, useModal } from './contexts/ModalContext';

// Hooks
import useSessionHeartbeat from './hooks/useSessionHeartbeat';

// Components
import { ModalRenderer } from './components/common/Modal';
import PrivacyConsentGate from './components/common/PrivacyConsentGate';
import RedirectConId from './routes/RedirectConId';
import ProtectedRoute from './routes/ProtectedRoute';
import { SECTION_PERMISSIONS } from './utils/permissions';
import ServerStatusBanner from './components/common/ServerStatusBanner';

// Pages (lazy loaded)
const Login = lazy(() => import('./pages/Login'));
const Registrazione = lazy(() => import('./pages/Registrazione')); // Pagina standalone multi-step
const RecuperoPassword = lazy(() => import('./pages/RecuperoPassword')); // Recupero password
const CompletamentoProfilo = lazy(() => import('./pages/CompletamentoProfilo')); // Pagina standalone completamento profilo
const InformativaFormazione   = lazy(() => import('./pages/InformativaFormazione'));   // Informativa Convegni — URL pubblica stabile
const InformativaAccount      = lazy(() => import('./pages/InformativaAccount'));      // Informativa Account — URL pubblica stabile
const PresaVisioneInformativa = lazy(() => import('./pages/PresaVisioneInformativa')); // Presa visione informativa — protetta
const Dashboard = lazy(() => import('./pages/Dashboard'));
const FormazioneIniziative = lazy(() => import('./pages/FormazioneIniziative'));
const Scadenzario = lazy(() => import('./pages/Scadenzario'));
const IniziativaPrenota = lazy(() => import('./pages/IniziativaPrenota'));
const IniziativaPrenotati = lazy(() => import('./pages/IniziativaPrenotati'));
const Collaboratori = lazy(() => import('./pages/Collaboratori'));
const IniziativaDetail = lazy(() => import('./pages/IniziativaDetail'));
const DocumentoPubblico = lazy(() => import('./pages/DocumentoPubblico'));
const Documenti = lazy(() => import('./pages/Documenti'));
const Articolo = lazy(() => import('./pages/Articolo'));
const Pagina = lazy(() => import('./pages/Pagina'));
const Convenzioni = lazy(() => import('./pages/Convenzioni'));
const ServizioConvenzione = lazy(() => import('./pages/ServizioConvenzione'));

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-anutel mx-auto mb-4"></div>
      <p className="text-gray-600 text-lg">Caricamento...</p>
    </div>
  </div>
);

/**
 * Componente per gestire il heartbeat della sessione
 * Invia periodicamente un ping al backend per mantenere attiva la sessione
 */
function SessionHeartbeatManager() {
  const { user } = useAuth();

  // Attiva heartbeat solo se l'utente è autenticato
  useSessionHeartbeat(!!user, 5 * 60 * 1000); // Ogni 5 minuti

  return null;
}

/**
 * Invia l'altezza del documento al parent tramite postMessage per auto-resize dell'iframe.
 * Quando un modal è aperto, invia window.innerHeight invece di scrollHeight:
 * il modal è position:fixed relativo al viewport dell'iframe, quindi l'iframe
 * deve essere esattamente il viewport — niente doppio scrollbar.
 */
function IframeResizeEmitter() {
  const { modals } = useModal();
  const hasModal = modals.length > 0;
  // Cache dell'altezza della pagina PRIMA che il modale aprisse.
  // Quando il modale si chiude, document.scrollHeight è ancora gonfiata
  // perché il parent non ha ancora ridimensionato l'iframe (min-height: 100vh
  // dentro l'iframe riflette ancora l'altezza del modale).
  const pageHeightRef = useRef(0);

  useEffect(() => {
    if (hasModal) return;

    // Alla chiusura del modale: resize iframe (backward compat) + notifica close per scroll restore
    if (pageHeightRef.current > 0) {
      window.parent.postMessage({ type: 'anutel-resize', height: pageHeightRef.current }, '*');
      window.parent.postMessage({ type: 'anutel-modal-close' }, '*');
    }

    // Salta il primo callback del ResizeObserver solo se abbiamo già inviato
    // la cached height (altrimenti al primo mount inviamo normalmente).
    let skipFirst = pageHeightRef.current > 0;

    const sendHeight = () => {
      if (skipFirst) { skipFirst = false; return; }
      const height = document.documentElement.scrollHeight;
      pageHeightRef.current = height;
      window.parent.postMessage({ type: 'anutel-resize', height }, '*');
    };

    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);

    return () => observer.disconnect();
  }, [hasModal]);

  return null;
}

/**
 * Componente interno che ascolta l'evento di logout e resetta i Context
 * Deve essere dentro i Provider per avere accesso ai Context
 */
function LogoutResetListener() {
  const { resetUIState } = useUI();
  const { resetModalState } = useModal();

  useEffect(() => {
    const handleGlobalLogout = () => {
      console.log('[App] Evento auth:logout ricevuto, reset di tutti i Context');
      // Reset UI Context (sidebar, overlay, toast, loading)
      resetUIState();
      // Reset Modal Context (chiude tutti i modali aperti)
      resetModalState();
    };

    window.addEventListener('auth:logout', handleGlobalLogout);

    return () => {
      window.removeEventListener('auth:logout', handleGlobalLogout);
    };
  }, [resetUIState, resetModalState]);

  return null;
}

// Box "Documenti utili" per le pagine fuori da /dashboard (quelle in /dashboard/* lo ricevono da MainLayout:
// un solo box per pagina), con larghezza e margini dei contenuti delle pagine pubbliche
function DocumentiSezioneBoxPubblico() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/dashboard')) return null;
  return (
    <div className="max-w-6xl mx-auto px-4 pb-8">
      <DocumentiSezioneBox />
    </div>
  );
}

function App() {
  return (
    <Router basename="/utente">
      <AuthProvider>
        <ProfileProvider>
          <ModalProvider>
            <UIProvider>
            {/* Session heartbeat per mantenere attiva la sessione */}
            <SessionHeartbeatManager />

            {/* Listener per reset completo al logout */}
            <LogoutResetListener />

            {/* Invia altezza al parent per auto-resize iframe */}
            <IframeResizeEmitter />

            {/* Barra "Server non raggiungibile" (in flusso: l'altezza arriva al parent via IframeResizeEmitter) */}
            <ServerStatusBanner className="sticky top-0 z-[35] px-4 py-2 space-y-1" />

            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/registrazione" element={<Registrazione />} />
                <Route path="/registrazione/verifica/:token" element={<Registrazione />} />
                <Route path="/recupero-password" element={<RecuperoPassword />} />
                <Route path="/informativa-formazione"    element={<InformativaFormazione />} />
                <Route path="/informativa-account"       element={<InformativaAccount />} />
                <Route path="/completamento-profilo"     element={<CompletamentoProfilo />} />
                <Route path="/presa-visione-informativa" element={<PresaVisioneInformativa />} />
                <Route path="/iniziativa/:id" element={<IniziativaDetail />} />
                <Route path="/documento/:id" element={<DocumentoPubblico variant="public" />} />
                <Route path="/documenti" element={<div className="main-container w-full px-4 py-8"><Documenti variant="public" /></div>} />
                {/* Dettaglio articolo: accesso deciso dal server per categoria (loggato → search-anutel, visitatore → middleware) */}
                <Route path="/articolo/:id" element={<div className="max-w-4xl mx-auto py-8 px-4"><Articolo /></div>} />
                <Route path="/pagina/:id" element={<div className="max-w-4xl mx-auto py-8 px-4"><Pagina /></div>} />
                <Route path="/associazione/articolo/:id" element={<RedirectConId to="/articolo" />} />
                <Route path="/associazione/pagina/:id" element={<RedirectConId to="/pagina" />} />
                <Route path="/convenzioni" element={<Convenzioni />} />
                <Route path="/convenzioni/servizio/:id" element={<ServizioConvenzione />} />

                {/* Dashboard routes (protected) */}
                <Route path="/dashboard/*" element={<Dashboard />} />

                {/* Formazione routes (protected, standalone layout) */}
                <Route path="/iniziative" element={<FormazioneIniziative />} />

                {/* Scadenzario stand-alone (link della notifica email): solo utenti con accesso alle Pubblicazioni */}
                <Route element={<ProtectedRoute allowedRoles={SECTION_PERMISSIONS.SCADENZARIO} section="SCADENZARIO" />}>
                  <Route path="/scadenzario" element={<Scadenzario />} />
                </Route>

                <Route path="/prenota/:id" element={<IniziativaPrenota />} />
                <Route path="/prenotati/:id" element={<IniziativaPrenotati />} />

                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <DocumentiSezioneBoxPubblico />
            </Suspense>

            {/* Avviso bloccante presa visione informativa aggiornata */}
            <PrivacyConsentGate />

            {/* Modal renderer (gestisce tutti i modali aperti) */}
            <ModalRenderer />
            </UIProvider>
          </ModalProvider>
        </ProfileProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
